"use client"
import { useRef, useState } from 'react'
import {
  type PlanId,
  type ResolvedBranchNode,
  type BranchOption,
} from '@/lib/branchTypes'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'
import {
  PlaybookBranchChoices,
  scrollBranchChoicesIntoView,
  settleScrollThenDeselect,
} from './PlaybookBranchChoices'
import { PlaybookBranchNodeMarkdownContent } from './PlaybookBranchNodeMarkdownContent'
import { ReadAnotherBranch } from './ReadAnotherBranch'

// Prose treatment for branch nodes. Inside the playbook content column
// (`.good-ending-text`), `.markdown-content` defaults to sans-serif via rules
// in globals.css. The recommended Plan A path opts back into the Tufte serif
// treatment via `primary-branch-prose`; other branches keep the sans default.
const PRIMARY_BRANCH_PROSE_CLASS = 'primary-branch-prose'

export type PlaybookBranchNodeProps = {
  nodeId: string
  rootId: string
  nodes: Record<string, ResolvedBranchNode>
  selectedPath: string[]
  setSelectedPath: (
    path: string[],
    options?: { scrollTargetNodeId?: string | null; updateUrl?: boolean }
  ) => void
  onPlanHover?: (planId: PlanId | null) => void
  onBranchHover?: (targetId: string | null) => void
  activePlan?: PlanId
  isEpilogueExpanded?: boolean
  // Which POV ending (if any) is expanded in place under the end-of-Plan-A
  // timeline diagram, and the bodies to render for them. Mutually exclusive with
  // the epilogue (see Playbook's ending state); rendered at the end cap alongside it.
  activePov?: Exclude<PerspectiveCategory, 'mainScenario'> | null
  perspectiveBodies?: { public?: string; insider?: string }
  selectedPerspective: PerspectiveCategory
  onShowPerspectiveAboveFlowchart: () => void
  onSelectPerspectiveFromLeaf: (category: PerspectiveCategory) => void
  showPerspectiveAboveFlowchart: boolean
}

export const PlaybookBranchNode = ({
  nodeId,
  rootId,
  nodes,
  selectedPath,
  setSelectedPath,
  onPlanHover,
  onBranchHover,
  activePlan,
  isEpilogueExpanded,
  activePov,
  perspectiveBodies,
  selectedPerspective,
  onShowPerspectiveAboveFlowchart,
  onSelectPerspectiveFromLeaf,
  showPerspectiveAboveFlowchart,
}: PlaybookBranchNodeProps) => {
  // Cancels an in-flight deselect settle-scroll (see handleBranchSelect); any
  // newer click supersedes the pending deselect.
  const pendingDeselectCancelRef = useRef<(() => void) | null>(null)
  const node = nodes[nodeId]

  if (!node) return null

  const branchPoints = node.branchPoints || []
  const currentPathIndex = selectedPath.indexOf(nodeId)
  const nextNodeId = currentPathIndex !== -1 && currentPathIndex < selectedPath.length - 1
    ? selectedPath[currentPathIndex + 1]
    : null
  const isLeaf =
    !nextNodeId
    && (node.branchPoints || []).every(bp => bp.options.length === 0)
    && nodeId !== rootId

  const handleBranchSelect = (targetId: string) => {
    pendingDeselectCancelRef.current?.()
    pendingDeselectCancelRef.current = null
    // At the choose-your-path node, clicking the already-selected plan toggles
    // it back OFF. The path truncation is deferred to settleScrollThenDeselect
    // so the viewport glides into the re-engaging scroll lock's bounds instead
    // of getting hard-clamped by it. (Only at this node: at follow-up
    // branchpoints useDefaultPrimarySelection would immediately re-push the
    // recommended plan, turning deselection into a confusing swap.)
    if (nextNodeId === targetId && node.expandedPlanLayout) {
      pendingDeselectCancelRef.current = settleScrollThenDeselect(nodeId, () => {
        pendingDeselectCancelRef.current = null
        setSelectedPath(selectedPath.slice(0, currentPathIndex + 1))
      })
      return
    }
    // Plan A is auto-selected at follow-up branchpoints (see
    // useDefaultPrimarySelection), so it's already `nextNodeId` before the
    // reader clicks it. Every other plan isn't. Either way a click should
    // reposition the buttons, so scroll unconditionally — but leave the path
    // alone when the target is already the selected branch. (Truncating the
    // path without re-pushing the target used to DEselect it: the choice-point
    // scroll lock re-engaged and its clamp cancelled the smooth scroll, which
    // is why Plan A didn't slide like the other plans.)
    if (nextNodeId !== targetId) {
      setSelectedPath([...selectedPath.slice(0, currentPathIndex + 1), targetId])
    }
    scrollBranchChoicesIntoView(nodeId)
  }
  const handleDefaultBranchSelect = (targetId: string) => {
    const newPath = selectedPath.slice(0, currentPathIndex + 1)
    newPath.push(targetId)
    setSelectedPath(newPath, { scrollTargetNodeId: null, updateUrl: false })
  }

  const options: BranchOption[] = branchPoints.length > 0 ? branchPoints[0].options : []
  // node.primary marks the recommended branch (the Plan A path, including the
  // root and continuation nodes). Only those nodes opt into the serif Tufte
  // treatment; every other branch keeps the default sans-serif.
  const isPrimaryBranch = !!node.primary

  // Leaf branches end on the Plan A timeline diagram. On the node that also
  // carries the epilogue (plan-a-root), the diagram is rendered *before* the
  // epilogue so it stays in view when the diagram's "Epilogue" label expands and
  // scrolls to it; on every other leaf it sits at the bottom as the end cap.
  const hasEpilogue = (node.sections || []).some(section => section.epilogueTitle)
  const readAnotherBranch = isLeaf ? (
    <ReadAnotherBranch
      selectedPath={selectedPath}
      onSelectBranch={(targetId) => {
        onShowPerspectiveAboveFlowchart()
        setSelectedPath([rootId, targetId])
      }}
    />
  ) : null

  return (
    <div id={node.anchor || node.id}>
      <PlaybookBranchNodeMarkdownContent
        nodeId={nodeId}
        node={node}
        isEpilogueExpanded={isEpilogueExpanded}
        activePov={activePov}
        perspectiveBodies={perspectiveBodies}
        onPlanHover={onPlanHover}
        onBranchHover={onBranchHover}
        beforeEpilogue={hasEpilogue ? readAnotherBranch : undefined}
        showScenarioEndCap={hasEpilogue}
        proseClassName={isPrimaryBranch ? PRIMARY_BRANCH_PROSE_CLASS : undefined}
      />
      <PlaybookBranchChoices
        nodeId={nodeId}
        options={options}
        nextNodeId={nextNodeId}
        selectedPath={selectedPath}
        currentPathIndex={currentPathIndex}
        nodes={nodes}
        onSelect={handleBranchSelect}
        onDefaultSelect={handleDefaultBranchSelect}
        onPlanHover={onPlanHover}
        onBranchHover={onBranchHover}
        showPerspectiveAboveFlowchart={
          nodeId === rootId && showPerspectiveAboveFlowchart
        }
        selectedPerspective={selectedPerspective}
        onSelectPerspective={onSelectPerspectiveFromLeaf}
      />
      {!hasEpilogue && readAnotherBranch}
      {nextNodeId && (
        <BranchContinuationReveal key={nextNodeId}>
          <PlaybookBranchNode
            nodeId={nextNodeId}
            rootId={rootId}
            nodes={nodes}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
            onPlanHover={onPlanHover}
            onBranchHover={onBranchHover}
            activePlan={activePlan}
            isEpilogueExpanded={isEpilogueExpanded}
            activePov={activePov}
            perspectiveBodies={perspectiveBodies}
            selectedPerspective={selectedPerspective}
            onShowPerspectiveAboveFlowchart={onShowPerspectiveAboveFlowchart}
            onSelectPerspectiveFromLeaf={onSelectPerspectiveFromLeaf}
            showPerspectiveAboveFlowchart={showPerspectiveAboveFlowchart}
          />
        </BranchContinuationReveal>
      )}
    </div>
  )
}

// How long a chosen branch's content takes to fade-and-rise in, and the
// window after client boot within which mounts count as "initial page
// render" (those skip the animation so the story never fades in on load).
const BRANCH_CONTINUATION_ENTER_MS = 400
const BRANCH_CONTINUATION_ENTER_EASING = "cubic-bezier(0.16, 1, 0.3, 1)"
const BRANCH_CONTINUATION_ENTER_RISE_PX = 10
const BRANCH_CONTINUATION_BOOT_GRACE_MS = 3000
const clientBootAt = typeof performance !== "undefined" ? performance.now() : 0

// Newly-chosen continuations fade-and-rise in instead of popping into the
// page (2026-07-09: picking a plan should read as a smooth reveal, not a
// jump cut). SSR/hydration mounts fall inside the boot grace window (and
// the server pass has no window at all), so the initially-rendered story
// never animates — only content the reader just chose does. Keyed by the
// continuation id at the call site so switching plans re-runs the reveal.
const BranchContinuationReveal = ({ children }: { children: React.ReactNode }) => {
  const [animateEntrance] = useState(
    () =>
      typeof window !== "undefined"
      && performance.now() - clientBootAt > BRANCH_CONTINUATION_BOOT_GRACE_MS,
  )
  return (
    <div
      className="mt-6"
      style={animateEntrance
        ? {
            animation: `playbook-branch-enter ${BRANCH_CONTINUATION_ENTER_MS}ms ${BRANCH_CONTINUATION_ENTER_EASING} backwards`,
          }
        : undefined}
    >
      {animateEntrance && (
        <style>{`@keyframes playbook-branch-enter { from { opacity: 0; transform: translateY(${BRANCH_CONTINUATION_ENTER_RISE_PX}px); } to { opacity: 1; transform: none; } }`}</style>
      )}
      {children}
    </div>
  )
}
